import type { Confidence } from '~/models/rulesOpinion'
import type { DerivedFacts } from './facts'
import type { AdherenceEvidence } from './prioritize'
import type { RulesThresholds } from './thresholds'

export interface ConfidenceAssessment {
  readonly level: Confidence
  readonly reason: string
}

/**
 * Confidence, as D-066 approved it (Checkpoint 2.3).
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  `high` IS NOW REACHABLE, AND THAT IS A DELIBERATE REVERSAL.
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Until Checkpoint 2.2 this ran a conservative subset and a test asserted `high`
 * never appeared. That test was correct **only while adherence was uncollected**
 * (D-060): two of the four conditions could not be evaluated, so emitting `high`
 * would have been a claim the data could not support. Checkpoints 2.0 and 2.1
 * now collect adherence and finalize weekly reviews, so all four conditions are
 * evaluable and the test has been replaced by one asserting this mapping. The
 * reversal was recorded in advance and is not a regression.
 *
 * The four conditions for `high`, all required:
 *
 *   1. both seven-day weigh-in windows sufficient (D-036)
 *   2. a comparable waist trend that did not trip the spread warning
 *   3. adherence at the **high** tier (≥6 of 7 days, all but one session)
 *   4. the same holding for `consecutiveWeeksForHighConfidence` weeks
 *
 * `moderate` is condition 1 with one of the rest missing or noisy. `low` is
 * everything else.
 *
 * **The old `low`-where-D-066-says-`moderate` gap closes here too**: a week with
 * enough weigh-ins but no waist trend is now `moderate`, not `low`.
 *
 * Confidence is not a probability, and must not be presented as one unless a
 * validated probability model is separately approved (rules-engine.md).
 */
export function assessConfidence(
  facts: DerivedFacts,
  adherence: AdherenceEvidence,
  weeksHoldingSteady: number,
  thresholds: RulesThresholds,
): ConfidenceAssessment {
  const comparable = facts.weight.weeklyChangePercent !== undefined

  // Condition 1 is a floor, not one vote among four. Without two comparable
  // weeks there is no trend to be confident about at all.
  if (!comparable) {
    return {
      level: 'low',
      reason:
        'There were not enough weigh-ins in both weeks to compare them, so this reading rests on very little.',
    }
  }

  const cleanWaistTrend = facts.waist.changeCm !== undefined && !facts.waist.latestQualityWarning
  const highAdherence = adherence === 'high'
  const persisted = weeksHoldingSteady >= thresholds.consecutiveWeeksForHighConfidence

  if (cleanWaistTrend && highAdherence && persisted) {
    return {
      level: 'high',
      reason:
        'Both weeks had enough weigh-ins, the waist measurement agrees with them, you followed the plan closely, and the picture has held for two weeks running.',
    }
  }

  return { level: 'moderate', reason: explainShortfall(facts, adherence, persisted) }
}

/**
 * What is keeping this at `moderate`.
 *
 * Names the specific missing condition rather than saying "some evidence is
 * missing", because the user can act on the first and not on the second. Where
 * several are missing it names the strongest one — the waist trend leads the
 * evidence hierarchy (D-005), so it is reported first.
 */
function explainShortfall(
  facts: DerivedFacts,
  adherence: AdherenceEvidence,
  persisted: boolean,
): string {
  if (facts.waist.changeCm === undefined) {
    return 'Both weeks had enough weigh-ins. Waist is the stronger signal for fat loss, and without two measurements to compare there is no waist trend yet.'
  }
  if (facts.waist.latestQualityWarning) {
    return 'Both weeks had enough weigh-ins, but the three waist readings varied by more than the tolerance, so this week’s waist figure is less reliable than usual.'
  }
  if (adherence === 'unknown') {
    return 'Both weeks had enough weigh-ins and the waist measurement agrees with them. Recording how closely you followed the plan is what would raise this further.'
  }
  if (adherence !== 'high') {
    return 'Both weeks had enough weigh-ins and the waist measurement agrees with them, but the plan was not followed closely enough this week for Project Sola to be sure the result reflects the plan itself.'
  }
  if (!persisted) {
    return 'Everything lines up this week. A second consistent week is what would raise this further — one week is not yet a pattern.'
  }
  return 'Both weeks had enough weigh-ins and the evidence broadly agrees.'
}
